import { useRef, useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Tiny "hold to speak, release to transcribe" mic button. Uses MediaRecorder +
 * POST /api/voice/transcribe (Whisper). Optional `transform` post-processes the
 * raw transcript before `onResult` is called (e.g., capitalize names, clean
 * extra whitespace).
 *
 * Works best on Chrome, Edge, Safari, iOS Safari — all MediaRecorder-capable.
 */
export default function VoiceInput({ onResult, transform, testId, hint = "Hold to speak", disabled }) {
    const [recording, setRecording] = useState(false);
    const [busy, setBusy] = useState(false);
    const chunks = useRef([]);
    const mediaRef = useRef(null);
    const streamRef = useRef(null);

    const stopStream = () => {
        try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch { /* ignore */ }
        streamRef.current = null;
    };

    const start = async () => {
        if (disabled || busy || recording) return;
        if (!navigator?.mediaDevices?.getUserMedia || typeof window.MediaRecorder === "undefined") {
            toast.error("Voice input isn't supported on this browser.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            // Prefer a type Whisper accepts natively; let the browser pick the best.
            const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""].find(m => !m || MediaRecorder.isTypeSupported(m)) || "";
            const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
            chunks.current = [];
            rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.current.push(e.data); };
            rec.onstop = async () => {
                stopStream();
                if (!chunks.current.length) { setRecording(false); return; }
                const type = rec.mimeType || "audio/webm";
                const blob = new Blob(chunks.current, { type });
                chunks.current = [];
                await upload(blob, type);
            };
            mediaRef.current = rec;
            rec.start();
            setRecording(true);
        } catch {
            toast.error("Couldn't access the microphone. Check browser permissions.");
            stopStream();
        }
    };

    const stop = () => {
        if (!recording) return;
        try { mediaRef.current?.stop(); } catch { /* ignore */ }
        setRecording(false);
    };

    const upload = async (blob, mime) => {
        setBusy(true);
        try {
            const fd = new FormData();
            // Whisper rejects files without an extension in the filename.
            const ext = mime.includes("mp4") ? "mp4" : mime.includes("wav") ? "wav" : "webm";
            fd.append("file", blob, `clip.${ext}`);
            fd.append("language", "en");
            const r = await api.post("/voice/transcribe", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 30000,
            });
            const text = (r.data?.text || "").trim();
            if (!text) { toast.message("Didn't catch that — try again"); return; }
            const processed = typeof transform === "function" ? transform(text) : text;
            onResult?.(processed);
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Transcription failed. Please try again.");
        } finally { setBusy(false); }
    };

    return (
        <button
            type="button"
            onMouseDown={start}
            onMouseUp={stop}
            onMouseLeave={stop}
            onTouchStart={(e) => { e.preventDefault(); start(); }}
            onTouchEnd={(e) => { e.preventDefault(); stop(); }}
            disabled={disabled || busy}
            title={recording ? "Release to transcribe" : hint}
            aria-label={recording ? "Release to transcribe" : hint}
            data-testid={testId}
            className={cn(
                "h-10 w-10 rounded-full inline-flex items-center justify-center shrink-0 transition-colors border",
                recording
                    ? "bg-destructive text-destructive-foreground border-destructive animate-pulse"
                    : busy
                        ? "bg-secondary text-muted-foreground border-border"
                        : "bg-card text-foreground border-border hover:bg-foreground hover:text-background",
                (disabled || busy) && !recording && "opacity-60 cursor-not-allowed"
            )}
        >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </button>
    );
}
